import type {
  GetDesignTypesByRequestResponse,
  GetDesignTypesResponse,
} from "@nlc/shared-types";

import { useQuery } from "@tanstack/react-query";

import { designTypesKeys } from "@/hooks/queryKeys.ts";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId.tsx";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import { useIsTemplate } from "@/hooks/useIsTemplate.tsx";
import apiClient from "@/lib/api/client.ts";

export function useGetDesignTypes() {
  const { data: request } = useGetRequest();
  const { isTemplate } = useIsTemplate();
  const projectId = useGetCurrentProjectId();

  return useQuery<
    GetDesignTypesByRequestResponse | GetDesignTypesResponse,
    Error,
    GetDesignTypesByRequestResponse
  >({
    enabled: !!request && !!projectId,
    queryFn: async () => {
      if (isTemplate) {
        return apiClient.url(`/design-types/picker/${projectId}`).get().json();
      }

      return apiClient.url(`/design-types/${request?.id}`).get().json();
    },
    queryKey: designTypesKeys.designTypesByRequestId(request?.id),
    select(data) {
      if (!("designTypes" in data))
        return data as GetDesignTypesByRequestResponse;

      const result: GetDesignTypesByRequestResponse = {
        subcategories: [],
      };

      for (const designType of data.designTypes.types) {
        if (designType.isBlocked) continue;

        const subcategoryIndex = result.subcategories.findIndex(
          (subcategory) => subcategory.id === designType.subcategoryId,
        );

        if (subcategoryIndex === -1) {
          result.subcategories.push({
            designTypes: [
              {
                defaultComplexityId: designType.defaultComplexityId,
                description: designType.description || "",
                id: designType.id,
                name: designType.name,
              },
            ],
            id: designType.subcategoryId,
            name:
              data.designTypes.subcategories.find(
                (subcategory) => subcategory.id === designType.subcategoryId,
              )?.name || "",
          });
        } else {
          result.subcategories[subcategoryIndex]!.designTypes.push({
            defaultComplexityId: designType.defaultComplexityId,
            description: designType.description || "",
            id: designType.id,
            name: designType.name,
          });
        }
      }

      return result;
    },
  });
}
